import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramDewPointEnthalpy } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';
import {
  dewPointFromDryBulbRh,
  enthalpyBtuPerLbDryAir,
  fToC,
  humidityRatioFromRh,
} from '../../../lib/calcs/psychrometrics';
import { formatNumber } from '../../../utils/format';

const KEY = 'tabai.calc.psychroDewPoint.v1';

type Fields = { tDryBulbF: string; rhPercent: string };
const EMPTY: Fields = { tDryBulbF: '75', rhPercent: '50' };

export default function PsychrometricsDewPointEnthalpyScreen() {
  const { unit, toImp, fromImp } = useUnits();
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ dewPointF: number; humidityRatio: number; enthalpyBtuLb: number } | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const tF = toImp(fields.tDryBulbF, 'temperature');
    const rh = Number(fields.rhPercent);

    if (!Number.isFinite(tF)) { setError('Dry-bulb temperature must be a number'); return; }
    if (!Number.isFinite(rh) || rh < 0 || rh > 100) { setError('Relative humidity must be between 0 and 100'); return; }

    try {
      const w = humidityRatioFromRh(fToC(tF), rh);
      const dp = dewPointFromDryBulbRh(tF, rh, 'F');
      const h = enthalpyBtuPerLbDryAir(tF, w);
      setResult({ dewPointF: dp.dewPoint, humidityRatio: w, enthalpyBtuLb: h });
    } catch {
      setError('Unable to calculate. Check inputs.');
    }
  };

  const clearAll = () => {
    setError(null);
    void clearPersisted();
    setResult(null);
  };

  const resultLines = result
    ? [
        { label: 'Dew Point', value: `${fromImp(result.dewPointF, 'temperature', 1)} ${unit('temperature')}` },
        { label: 'Humidity Ratio (W)', value: `${fromImp(result.humidityRatio, 'humidityRatio', 5)} ${unit('humidityRatio')}` },
        { label: 'Enthalpy', value: `${fromImp(result.enthalpyBtuLb, 'enthalpy', 1)} ${unit('enthalpy')}`, emphasis: true as const },
      ]
    : null;

  return (
    <CalcShell
      title="Psychrometrics"
      subtitle="Dew point, humidity ratio, and enthalpy from dry-bulb and RH"
      formula={
        <>
          <FormulaLine>W = 0.621945 · Pv / (P − Pv)</FormulaLine>
          <FormulaLine>h = 0.240·Tdb + W·(1061 + 0.444·Tdb)</FormulaLine>
          <FormulaNote>Internal I-P units; display converts automatically.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={resultLines}
      copyFooter="Formula: Psychrometrics (W, h)"
      diagram={<DiagramDewPointEnthalpy />}
      outputSlots={{
        dry_bulb_f: fields.tDryBulbF ? toImp(fields.tDryBulbF, 'temperature') : undefined,
        rh_pct: fields.rhPercent ? parseFloat(fields.rhPercent) : undefined,
        dewpoint_f: result ? result.dewPointF : undefined,
        humidity_ratio_lb_lb: result ? result.humidityRatio : undefined,
        enthalpy_btu_lb: result ? result.enthalpyBtuLb : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.dry_bulb_f != null && !fields.tDryBulbF) {
          patch.tDryBulbF = fromImp(values.dry_bulb_f, 'temperature', 1);
        }
        if (values.rh_pct != null && !fields.rhPercent) {
          patch.rhPercent = values.rh_pct.toFixed(1);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Dry-bulb"
          value={fields.tDryBulbF}
          onChangeText={(v) => set({ tDryBulbF: v })}
          rightText={unit('temperature')}
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Relative Humidity"
          value={fields.rhPercent}
          onChangeText={(v) => set({ rhPercent: v })}
          rightText="%"
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
}
